// Third party
import React from 'react';
import classNames from 'classnames';

// Components
import ProposalPatternSelector from './components/ProposalPatternSelector';
import ProposalColorSelector from './components/ProposalColorSelector';
import ProposalTemplateButton from './components/ProposalTemplateSelector';
import ProposalLanguageSelector from './components/ProposalLanguageSelector';
import ProposalOrientationSelector from './components/ProposalOrientationSelector';
import ProposalPaperFormatSelector from './components/ProposalPaperFormatSelector';
import ProposalItemSelector from './components/ProposalItemSelector';
import ProposalItemLister from './components/proposal-item-lister/ProposalItemLister';
import ProposalSections from './components/ProposalSections';
import EditProposalAdvisorInfo from './components/ProposalAdvisoryInfoEditor';
import ProposalCoBrandingColors from './components/ProposalCoBrandingColors';

// Utils
import { userHasPermissions } from '../../../../utils/permissions';
import { getMaxNumberOfItems } from '../../ProposalHelpers';
import { DropItemPosition } from '../../../../utils/drag-and-drop';
import { PROPOSAL_BANNER_STYLES, PROPOSAL_ORIENTATION } from '../../utils/constants';

// Namespaces
import * as ProposalNS from '../../Proposal';
import { ProposalTemplateOptions } from '../../../../redux/modules/types/proposal-v2';
import ProposalPaperFormat from '@/types/proposal-paper-format';
import User from '@/types/user';
import Locale from '@/types/locale';
import Comparison from '@/types/comparison';
import ComparisonItem from '@/types/comparison-item';
import ProposalOrientation from '@/types/proposal-orientation';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';
import { CollapsibleContainer } from '@/views/components/misc';
import { formatMessage } from '@/utils/i18n';

export enum ProposalSidebarOptions {
  TEMPLATE = 'TEMPLATE',
  ITEM_SELECTOR = 'ITEM_SELECTOR',
  ITEM_LISTER = 'ITEM_LISTER',
  LANGUAGE = 'LANGUAGE',
  PROPOSAL_SECTIONS = 'PROPOSAL_SECTIONS',
  PATTERN_SELECTOR = 'PATTERN_SELECTOR',
  COLOR_SELECTOR = 'COLOR_SELECTOR',
  ORIENTATION = 'ORIENTATION',
  PAPER_FORMAT = 'PAPER_FORMAT',
  EDIT_PROPOSAL_ADVISOR_INFO = 'EDIT_PROPOSAL_ADVISOR_INFO',
  COBRANDING_COLOR = 'COBRANDING_COLOR',
}

type Props = {
  user: User;
  locale: Locale;
  proposal: ProposalNS.Proposal;
  comparison: Comparison;
  selectedTab: ProposalNS.ProposalSidebarTab;
  onToggleSection: (section: string, selected: boolean) => void;
  onToggleDivision: (division: string, selected: boolean) => void;
  onToggleGifBaseScenario: (value: boolean) => void;
  onItemsSelection: (items: ComparisonItem[]) => void;
  onItemPurposeFlagUpdate: (itemId: string, flags: string[]) => void;
  onGifItemsSelection: (items: BMOGIFItem[]) => void;
  onChangeTemplate: () => void;
  onLocaleChange: (locale: Locale) => void;
  onProposalStyleOptionsChange: (styleOptions: ProposalNS.ProposalStyleOptionsV2) => void;
  proposalTemplateOptions: ProposalTemplateOptions;
  templates: ProposalNS.BaseProposalTemplate[];
  gifHideBaseScenario?: boolean;
  onToggleShortHistory: (value: boolean) => void;
  onEditBrandingInfo: () => void;
  pageIndexes?: ProposalNS.ProposalSectionPageMapping;
  onChangeItemOrder: (itemId: string, referenceId: string, position?: DropItemPosition) => void;
  onJumpToPage: (section: string) => void;
};

class ProposalSidebar extends React.PureComponent<Props> {
  onLocaleChange = (locale: Locale) => {
    const { proposal, onLocaleChange } = this.props;

    if (locale !== proposal.options.locale) {
      onLocaleChange(locale);
    }
  };

  onPaperFormatChange = (paperFormat: ProposalPaperFormat) => {
    const {
      proposalTemplateOptions: { styleOptions },
    } = this.props;
    if (paperFormat !== styleOptions.paperFormat) {
      this.props.onProposalStyleOptionsChange({
        ...styleOptions,
        paperFormat,
      });
    }
  };

  onToggleFundProfiles = (value: boolean) => {
    const {
      proposalTemplateOptions: { styleOptions },
      onProposalStyleOptionsChange,
    } = this.props;

    if (styleOptions.includeFundProfiles !== value) {
      const newOptions: ProposalNS.ProposalStyleOptionsV2 = {
        ...styleOptions,
        includeFundProfiles: value,
      };

      onProposalStyleOptionsChange(newOptions);
    }
  };

  onTogglePortfolioProfiles = (value: boolean) => {
    const {
      proposalTemplateOptions: { styleOptions },
    } = this.props;

    if (styleOptions.includePortfolioProfiles !== value) {
      const newOptions: ProposalNS.ProposalStyleOptionsV2 = {
        ...styleOptions,
        includePortfolioProfiles: value,
      };

      this.props.onProposalStyleOptionsChange(newOptions);
    }
  };

  onToggleGroupProfiles = (value: boolean) => {
    const {
      proposalTemplateOptions: { styleOptions },
    } = this.props;

    if (styleOptions.includeGroupProfiles !== value) {
      const newOptions: ProposalNS.ProposalStyleOptionsV2 = {
        ...styleOptions,
        includeGroupProfiles: value,
      };

      this.props.onProposalStyleOptionsChange(newOptions);
    }
  };

  onOrientationChange = (orientation: ProposalOrientation) => {
    const {
      proposalTemplateOptions: { styleOptions },
      proposal: { items, template },
    } = this.props;

    const maxProposalItems = getMaxNumberOfItems(template, orientation);

    if (items.length > maxProposalItems) {
      const data = items.slice(0, maxProposalItems);
      this.props.onItemsSelection(data);
    }

    const newOptions: ProposalNS.ProposalStyleOptionsV2 = {
      ...styleOptions,
      orientation,
    };

    this.props.onProposalStyleOptionsChange(newOptions);
  };

  onHeaderPatternChange = (pattern: string) => {
    const {
      proposalTemplateOptions: { styleOptions },
    } = this.props;

    const newOptions: ProposalNS.ProposalStyleOptionsV2 = {
      ...styleOptions,
      banners: {
        ...styleOptions.banners,
        pattern,
      },
    };

    this.props.onProposalStyleOptionsChange(newOptions);
  };

  onHeaderColorChange = (headerColor: string) => {
    const {
      proposalTemplateOptions: { styleOptions },
    } = this.props;

    const style = PROPOSAL_BANNER_STYLES.find(s => s.background === headerColor);

    if (!style) {
      return;
    }

    const newOptions: ProposalNS.ProposalStyleOptionsV2 = {
      ...styleOptions,
      banners: {
        ...styleOptions.banners,
        ...style,
      },
    };

    this.props.onProposalStyleOptionsChange(newOptions);
  };

  onCoBrandingColorChange = (selectedColor: string, key: string) => {
    const {
      proposalTemplateOptions: { styleOptions },
    } = this.props;

    const style = {
      coBrandingColors: {
        [key]: selectedColor,
      },
    };

    const newOptions: ProposalNS.ProposalStyleOptionsV2 = {
      ...styleOptions,
      banners: {
        ...styleOptions.banners,
        ...style,
      },
    };

    this.props.onProposalStyleOptionsChange(newOptions);
  };

  renderElement = (element: ProposalNS.ProposalSidebarElement, key: string) => {
    const { proposalTemplateOptions, proposal, comparison, user, templates, gifHideBaseScenario, locale, pageIndexes } = this.props;
    const {
      items,
      template,
      template: { customParameters },
    } = proposal;
    const { allowedItemTypes } = template.itemManagement;
    const { itemSelectors } = element as ProposalNS.ProposalItemSelection;
    const {
      styleOptions: { orientation },
    } = proposalTemplateOptions;

    let renderedElement;
    const maxItems: number = getMaxNumberOfItems(template, orientation);

    switch (element.type) {
      case ProposalSidebarOptions.PROPOSAL_SECTIONS: {
        // Check if proposal has any items - if it's only GIF items, skip content selection.
        if (proposal.items.length === 0) {
          break;
        }

        renderedElement = (
          <ProposalSections
            user={user}
            pageIndexes={pageIndexes}
            proposal={proposal}
            onToggleSection={this.props.onToggleSection}
            onToggleDivision={this.props.onToggleDivision}
            selectedSections={proposalTemplateOptions.sections}
            selectedDivisions={proposalTemplateOptions.divisions}
            onJumpToPage={this.props.onJumpToPage}
          />
        );
        break;
      }
      case ProposalSidebarOptions.ITEM_SELECTOR: {
        renderedElement = (
          <ProposalItemSelector
            onSelection={this.props.onItemsSelection}
            comparisonItems={comparison.items}
            selectedProposalItems={items}
            numberOfControls={maxItems}
            itemSelectors={itemSelectors}
            singleItemShift={customParameters && customParameters.singleItemShift}
            allowedItemTypes={allowedItemTypes}
          />
        );
        break;
      }
      case ProposalSidebarOptions.ITEM_LISTER: {
        renderedElement = (
          <CollapsibleContainer className="proposal-item-lister" headerText={formatMessage('Proposal.ItemSelection')} headerTag="h2">
            <ProposalItemLister
              user={user}
              locale={locale}
              onItemsSelection={this.props.onItemsSelection}
              onItemPurposeFlagUpdate={this.props.onItemPurposeFlagUpdate}
              onGifItemsSelection={this.props.onGifItemsSelection}
              onToggleGifBaseScenario={this.props.onToggleGifBaseScenario}
              onToggleFundProfiles={this.onToggleFundProfiles}
              onTogglePortfolioProfiles={this.onTogglePortfolioProfiles}
              onToggleGroupProfiles={this.onToggleGroupProfiles}
              onToggleShortHistory={this.props.onToggleShortHistory}
              proposal={proposal}
              comparison={comparison}
              allowedItemTypes={allowedItemTypes}
              maxItemsNumber={maxItems}
              gifHideBaseScenario={gifHideBaseScenario}
              showIncludeFundProfiles={element.showIncludeFundProfiles}
              showIncludePortfolioProfiles={element.showIncludePortfolioProfiles}
              showIncludeGroupProfiles={element.showIncludeGroupProfiles}
              showShortHistory={element.showShortHistory}
              showProposalItemPurposeSelector={element.showProposalItemPurposeSelector}
              itemColorOverride={element.itemColorOverride}
              onChangeItemOrder={this.props.onChangeItemOrder}
            />
          </CollapsibleContainer>
        );
        break;
      }
      case ProposalSidebarOptions.LANGUAGE: {
        const {
          appConfig: { appLocales },
        } = user;

        renderedElement = <ProposalLanguageSelector onLocaleChange={this.onLocaleChange} selectedLocale={proposal.options.locale} appLocales={appLocales} />;
        break;
      }
      case ProposalSidebarOptions.TEMPLATE: {
        if (templates.length > 1) {
          renderedElement = <ProposalTemplateButton onChangeTemplate={this.props.onChangeTemplate} />;
        }
        break;
      }
      case ProposalSidebarOptions.EDIT_PROPOSAL_ADVISOR_INFO: {
        renderedElement = <EditProposalAdvisorInfo onEditBrandingInfo={this.props.onEditBrandingInfo} />;
        break;
      }
      case ProposalSidebarOptions.PATTERN_SELECTOR: {
        renderedElement = <ProposalPatternSelector onHeaderPatternChange={this.onHeaderPatternChange} styleOptions={proposalTemplateOptions.styleOptions} />;
        break;
      }
      case ProposalSidebarOptions.COLOR_SELECTOR: {
        renderedElement = <ProposalColorSelector onHeaderColorChange={this.onHeaderColorChange} styleOptions={proposalTemplateOptions.styleOptions} />;
        break;
      }
      case ProposalSidebarOptions.COBRANDING_COLOR: {
        renderedElement = (
          <ProposalCoBrandingColors onUpdateCoBrandedColors={this.onCoBrandingColorChange} styleOptions={proposalTemplateOptions.styleOptions} user={user} />
        );
        break;
      }
      case ProposalSidebarOptions.ORIENTATION: {
        renderedElement = (
          <ProposalOrientationSelector
            onOrientationChange={this.onOrientationChange}
            selectedOrientation={proposalTemplateOptions.styleOptions.orientation || PROPOSAL_ORIENTATION.PORTRAIT}
          />
        );
        break;
      }
      case ProposalSidebarOptions.PAPER_FORMAT: {
        renderedElement = (
          <ProposalPaperFormatSelector onPaperFormatChange={this.onPaperFormatChange} selectedFormat={proposalTemplateOptions.styleOptions.paperFormat} />
        );
        break;
      }
    }

    // TODO - remove when we get to online proposals
    const classes = classNames('proposal-sidebar-section', {
      MD: ['MD_PIC', 'MD_SIGNATURE'].includes(proposal.template.key),
      'border-top': element.borders && element.borders.includes('TOP'),
      'border-bottom': element.borders && element.borders.includes('BOTTOM'),
    });

    return !renderedElement ? null : (
      <div className={classes} key={key}>
        {renderedElement}
      </div>
    );
  };

  getTabElements = () => {
    const { selectedTab, user } = this.props;

    return selectedTab.elements.filter(
      element => element.permissions === undefined || (element.permissions !== undefined && userHasPermissions(user, element.permissions)),
    );
  };

  render() {
    return (
      // TODO - remove v2 in class name when we fully switch to the new builder. Added so we don't inherit styles from the old builder
      <div className="proposal-sidebar-v2">
        <div className="proposal-sidebar-content">{this.getTabElements().map(element => this.renderElement(element, `tab ${element.type}`))}</div>
      </div>
    );
  }
}

export default ProposalSidebar;
